// views/Dashboard/Bonuses/components/BonusCard/BonusCard.tsx
import { useState } from 'react';
import CountdownTimer from '../CountdownTimer/CountdownTimer';
import RiskGauge from '../RiskGauge/RiskGauge';
import Button from '../../../../../components/common/Button/Button';
import Modal from '../../../../../components/common/Modal/Modal';
import { BonusType, Risk, SpecialMark } from '../../../../../Entities';
import { notify } from '../../../../../components/common/Toast/toastBridge';
import styles from './BonusCard.module.css';

interface BonusCardProps {
  bonus: any;
  onDetail: () => void;
  hideBonus: (bonus: any) => Promise<void>;
  locked?: boolean;
}

const BonusCard = ({ bonus, onDetail, hideBonus }: BonusCardProps) => {
  const [deletionModal, setDeletionModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  const onHide = async () => {
    try {
      setProcessing(true);
      await hideBonus(bonus);
      setDeletionModal(false);
    } catch (error) {
      notify.error('Nepodařilo se skrýt bonus');
    } finally {
      setProcessing(false);
    }
  };

  const typeBadge = BonusType.get({ name: bonus.bonus_type_name });
  const risk = Risk.values.filter((r) => r.rate === bonus.risk_rate)[0];

  if (deletionModal) {
    return (
      <Modal isOpen onClose={() => setDeletionModal(false)} title="Smazat bonus" size="medium">
        <div className={styles.deletingBonusModal}>
          <p>
            Opravdu chcete <strong>nenávratně</strong> skrýt tento bonus?
          </p>
          <div className={styles.actions}>
            <Button variant="ghost" onClick={() => setDeletionModal(false)}>
              ZRUŠIT
            </Button>
            <Button disabled={processing} variant="warning" size="small" onClick={onHide}>
              ANO
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const specialMark = bonus.special_mark ? SpecialMark.get({ name: bonus.special_mark }) : null;
  const specialColor = specialMark?.color || null;

  return (
    <div
      className={`${styles.bonusCard} ${bonus.special_mark ? styles.specialCard : ''}`}
      style={specialColor ? { border: `2px solid ${specialColor}`, backgroundColor: specialColor } : undefined}
    >
      {specialMark && (
        <div
          className={styles.generalBadge}
          style={{ backgroundColor: specialColor || undefined, borderColor: specialColor || undefined }}
        >
          {specialMark.label}
        </div>
      )}

      <div className={styles.topRightCluster}>
        <RiskGauge risk={risk} levels={Risk.values} size="small" />
        <CountdownTimer startDate={bonus.start_timestamp * 1000} endDate={bonus.end_timestamp * 1000} size="small" />
      </div>

      <div className={styles.header}>
        <div className={styles.companyInfo}>
          <div className={styles.info}>
            <div className={styles.titleRow}>
              <h3>{bonus.name}</h3>
            </div>
            <p className={styles.company}>{bonus.company.name}</p>
            <span
              style={{ color: typeBadge.color, backgroundColor: typeBadge.background_color }}
              className={styles.typeBadge}
            >
              {typeBadge.name}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.details}>
        <div className={styles.amountRow}>
          <p className={styles.amount}>{Number(bonus.expected_value)} Kč</p>
        </div>
        <p className={styles.description}>{bonus.description}</p>

        {bonus.step_titles && (
          <div className={styles.quickSteps}>
            <p className={styles.stepsTitle}>Rychlé kroky:</p>
            <ul className={styles.stepsList}>
              {bonus.step_titles.slice(0, 2).map((step_title: string, index: number) => (
                <li key={index}>{step_title}</li>
              ))}
              {bonus.step_titles.length > 2 && (
                <li className={styles.moreSteps}>+{bonus.step_titles.length - 2} dalších kroků</li>
              )}
            </ul>
          </div>
        )}

        <div className={styles.footer}>
          <div className={styles.actions}>
            <Button
              disabled={processing}
              variant="quartery"
              size="small"
              className={styles.hideButton}
              onClick={() => setDeletionModal(true)}
            >
              🗑️
            </Button>
            {bonus.step_idx !== 0 ? (
              <Button variant="quartery" onClick={onDetail} data-tour="bonus-open-btn">
                Pokračovat →
              </Button>
            ) : (
              <Button variant="primary" onClick={onDetail} data-tour="bonus-open-btn">
                Začít
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BonusCard;
